<dx-popup
  [visible]="visible"
  (visibleChange)="onPopupVisibleChange($event)"
  title="Bulk payment link"
  width="min(900px, 95vw)"
  [height]="'auto'"
  maxHeight="90vh"
  [fullScreen]="isSmall"
  [dragEnabled]="true"
  [closeOnOutsideClick]="false"
  [showCloseButton]="true"
  [elementAttr]="{ 'data-testid': 'lump-links-popup' }"
>
  <div *dxTemplate="let data of 'content'" class="lump-links-body">
    <!-- Status panel: this OG's open bulk batch. Historical (expired/succeeded)
         batches don't render here — the builder takes over once nothing is open. -->
    <ng-container *ngIf="hasOpenBatch">
      <h6>Bulk payment link</h6>
      <p *ngIf="viewingOtherBatch" class="mb-1">
        <a class="lump-builder-items-toggle" (click)="backToBuilder()" data-testid="lump-links-back">‹ Back to orders</a>
      </p>
      <div class="table-responsive">
        <table class="table table-sm" data-testid="lump-links-table">
          <thead>
            <tr>
              <th>Order</th><th>PO</th><th>Facility</th><th>Amount</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let link of batch.links">
              <td data-label="Order">{{ link.order_group_id }}</td>
              <td data-label="PO">{{ linkPo(link) }}</td>
              <td data-label="Facility">{{ linkFacility(link) }}</td>
              <td data-label="Amount">{{ link.amount | currency }}</td>
              <td data-label="Status">
                <span class="lump-status-pill" [ngClass]="statusPillClass(link.status)">{{ statusLabel(link.status) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p data-testid="lump-links-summary">Total {{ batch.total | currency }}</p>
      <div class="lump-links-actions">
        <dx-button
          text="Copy link"
          (onClick)="copyLink()"
          [elementAttr]="{ 'data-testid': 'lump-links-btn-copy' }"
        ></dx-button>
        <dx-button
          *ngIf="canCancel"
          text="Cancel Payment Links"
          type="danger"
          stylingMode="outlined"
          icon="clear"
          class="ml-1"
          (onClick)="cancelBatch()"
          [elementAttr]="{ 'data-testid': 'lump-links-btn-cancel' }"
        ></dx-button>
      </div>
    </ng-container>

    <!-- Skeleton while loading — mimics the builder rows so the popup
         doesn't sit empty (or worse, show the previous company's data) -->
    <div *ngIf="loading && !hasOpenBatch" class="lump-skeleton" data-testid="lump-links-skeleton">
      <div class="lump-skeleton__bar lump-skeleton__bar--title"></div>
      <div class="lump-skeleton__row" *ngFor="let s of skeletonRows">
        <span class="lump-skeleton__dot"></span>
        <span class="lump-skeleton__lines">
          <span class="lump-skeleton__bar lump-skeleton__bar--wide"></span>
          <span class="lump-skeleton__bar lump-skeleton__bar--narrow"></span>
        </span>
        <span class="lump-skeleton__bar lump-skeleton__bar--amount"></span>
      </div>
    </div>

    <!-- Builder: hidden while a batch is open on this OG -->
    <ng-container *ngIf="!hasOpenBatch && !loading">
      <h6>{{ companyName }} — unpaid orders</h6>
      <p class="text-muted lump-builder-intro" data-testid="lump-links-intro">
        Combine unpaid orders into one payment link. The link is emailed to the
        recipient, who pays all selected orders in a single Stripe checkout.
        While a link is open, on-site payment for its orders is disabled — you
        can cancel it at any time before it is paid.
      </p>
      <div
        class="lump-builder-row"
        *ngFor="let o of pagedOrders"
        [class.lump-builder-row--disabled]="!o.eligible"
      >
        <div class="d-flex align-items-center flex-wrap">
          <dx-check-box
            class="mr-2"
            [(value)]="o.checked"
            [disabled]="!o.eligible"
            [elementAttr]="{ 'data-testid': 'lump-links-check-order' }"
          ></dx-check-box>
          <span class="mr-2 lump-builder-og"><strong>Order #{{ o.id }}</strong></span>
          <span class="mr-2 text-muted" *ngIf="o.created_date">{{ o.created_date }}</span>
          <span class="mr-2 lump-builder-facility flex-grow-1">{{ o.facility }}</span>
          <span class="lump-builder-amount"><strong>{{ o.buyer_total | currency }}</strong></span>
        </div>
        <div class="lump-builder-sub d-flex align-items-center flex-wrap">
          <span class="mr-3 text-muted lump-builder-po" *ngIf="o.po">PO {{ o.po }}</span>
          <a
            class="mr-2 lump-builder-items-toggle"
            *ngIf="o.items?.length"
            (click)="toggleItems(o)"
            data-testid="lump-links-toggle-items"
          >{{ o.items.length }} item{{ o.items.length === 1 ? '' : 's' }} {{ o.expanded ? '▾' : '▸' }}</a>
          <span class="text-muted lump-builder-reason" *ngIf="!o.eligible">
            {{ o.reason }}
            <a
              *ngIf="o.reason === 'In another bulk payment'"
              class="lump-builder-items-toggle ml-1"
              (click)="viewBatch(o.id)"
              data-testid="lump-links-view-batch"
            >view ›</a>
          </span>
        </div>
        <ul class="lump-builder-items" *ngIf="o.expanded">
          <li *ngFor="let it of o.items">
            {{ it.name }} <span class="text-muted">× {{ it.quantity }}</span>
          </li>
        </ul>
      </div>
      <p *ngIf="!loading && !orders.length" class="text-muted" data-testid="lump-links-empty">
        No unpaid orders for this company.
      </p>
      <div
        class="lump-builder-pager d-flex align-items-center"
        *ngIf="orders.length > pageSize"
        data-testid="lump-links-pager"
      >
        <dx-button
          icon="chevronleft"
          stylingMode="text"
          [disabled]="page === 0"
          (onClick)="setPage(page - 1)"
          [elementAttr]="{ 'data-testid': 'lump-links-pager-prev' }"
        ></dx-button>
        <span class="mx-2 text-muted">Page {{ page + 1 }} of {{ pageCount }}</span>
        <dx-button
          icon="chevronright"
          stylingMode="text"
          [disabled]="page >= pageCount - 1"
          (onClick)="setPage(page + 1)"
          [elementAttr]="{ 'data-testid': 'lump-links-pager-next' }"
        ></dx-button>
      </div>
      <div class="lump-builder-footer d-flex align-items-center flex-wrap">
        <span class="mr-2" data-testid="lump-links-summary-selected">
          Selected {{ selected.length }} — {{ total | currency }}
        </span>
        <dx-select-box
          class="mr-2 flex-grow-1 lump-recipient"
          [items]="recipientEmails"
          [acceptCustomValue]="true"
          [searchEnabled]="true"
          placeholder="Recipient email"
          [(value)]="recipient"
          [elementAttr]="{ 'data-testid': 'lump-links-select-recipient' }"
        ></dx-select-box>
        <dx-button
          class="ml-auto"
          type="default"
          text="Generate & Send"
          [disabled]="!selected.length || !recipient || generating"
          (onClick)="generate()"
          [elementAttr]="{ 'data-testid': 'lump-links-btn-generate' }"
        ></dx-button>
      </div>
    </ng-container>
  </div>
</dx-popup>
